import React from 'react';
import f1DriversStandings from '../assets/F1TrackerApp.jpeg';
import scuWebsiteImage from '../assets/ThetaTauApp.png';

const Portfolio = () => {
    const projects = [
        {
            title: "SCU Theta Tau",
            subtitle: "Upsilon Epsilon Chapter",
            year: "2024",
            category: "Full Stack",
            description: "Official digital presence for Santa Clara University's Professional Engineering Fraternity. Bridges public recruitment needs with internal member management systems.",
            features: [
                { title: "Member Portal", desc: "Secure role-based access control via Firebase Authentication for 60+ active members." },
                { title: "Data Management", desc: "Scalable Firestore database schema for tracking recruitment pipelines and member directories." }
            ],
            tags: ["React.js", "Firebase Auth", "Firestore", "Tailwind CSS"],
            image: scuWebsiteImage,
            link: "https://www.scuthetatau.com/"
        },
        {
            title: "Formula 1 Tracker",
            subtitle: "Global Racing Stats",
            year: "2024",
            category: "Web App",
            description: "A real-time dashboard tracking Formula 1 driver standings, race results, and constructor statistics. Provides fans with instant access to seasonal performance data.",
            features: [
                { title: "Data Integration", desc: "Fetches and visualizes racing statistics through third-party APIs." },
                { title: "Responsive Interface", desc: "Optimized for seamless access across desktop and mobile devices." }
            ],
            tags: ["React.js", "API Integration", "Data Visualization", "Vercel"],
            image: f1DriversStandings,
            link: "https://formula1-tracker.vercel.app/"
        },
        {
            title: "Trash Classification",
            subtitle: "Recycling Assistant",
            year: "2024",
            category: "Mobile ML",
            description: "Led development of an ML-powered mobile application that identifies compostable vs. recyclable items in real-time. Built to solve campus waste sorting inefficiency through instant visual feedback.",
            features: [
                { title: "Custom ML Model", desc: "Collected/labeled a custom dataset and trained a convolutional neural network using TensorFlow/Python." },
                { title: "Cross-Platform App", desc: "Developed Flutter frontend with real-time camera integration and Flask REST API for inference." }
            ],
            tags: ["TensorFlow", "Python", "Flutter", "Flask", "REST API"],
            image: "https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?q=80&w=2670&auto=format&fit=crop",
            link: null
        }
    ];

    return (
        <main className="pt-32 pb-24 min-h-screen bg-background-dark">
            <div className="max-w-[1400px] mx-auto px-8">
                <div className="flex items-center gap-4 mb-16">
                    <span className="w-12 h-[2px] bg-primary"></span>
                    <h1 className="text-5xl md:text-7xl font-black sports-font text-white">
                        FEATURED <span className="text-primary">WORK</span>
                    </h1>
                </div>

                <div className="space-y-32">
                    {projects.map((project, index) => (
                        <div key={index} className="grid lg:grid-cols-12 gap-12 bg-surface-dark border border-white/5 p-8 md:p-12 group hover:border-white/10 transition-all">
                            <div className={`lg:col-span-7 flex flex-col justify-center order-2 ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                                <div className="flex items-center gap-4 mb-6">
                                    <span className="px-3 py-1 bg-primary text-black font-bold text-xs uppercase tracking-widest">{project.category}</span>
                                    <span className="text-white/40 font-mono text-sm">{project.year}</span>
                                </div>

                                <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase">
                                    {project.title} <br />
                                    <span className="text-white/50">{project.subtitle}</span>
                                </h2>

                                <p className="text-white/70 text-lg leading-relaxed mb-8 font-light">
                                    {project.description}
                                </p>

                                <div className="space-y-6 mb-10">
                                    {project.features.map((feature, i) => (
                                        <div key={i} className="flex items-start gap-4">
                                            <span className="material-symbols-outlined text-primary mt-1">check_circle</span>
                                            <div>
                                                <h4 className="text-white font-bold uppercase text-sm tracking-wider mb-1">{feature.title}</h4>
                                                <p className="text-white/50 text-sm">{feature.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex flex-wrap gap-2 mb-8">
                                    {project.tags.map((tag, i) => (
                                        <span key={i} className="text-xs font-mono text-white/40 border border-white/10 px-3 py-1 bg-black/20">
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                {project.link && (
                                    <div>
                                        <a href={project.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-white font-bold uppercase tracking-widest text-xs hover:text-primary transition-colors">
                                            View Project
                                            <span className="material-symbols-outlined text-lg">arrow_outward</span>
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div className={`lg:col-span-5 relative order-1 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'} min-h-[300px] lg:min-h-full`}>
                                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent opacity-50 z-10"></div>
                                <div className="w-full h-full bg-cover bg-center grayscale group-hover:grayscale-0 transition-all duration-700 border border-white/10" style={{ backgroundImage: `url("${project.image}")` }}></div>

                                {/* Abstract overlays */}
                                <div className={`absolute -bottom-4 ${index % 2 === 0 ? '-right-4' : '-left-4'} w-24 h-24 border-b-2 ${index % 2 === 0 ? 'border-r-2' : 'border-l-2'} border-primary`}></div>
                                <div className={`absolute -top-4 ${index % 2 === 0 ? '-left-4' : '-right-4'} w-24 h-24 border-t-2 ${index % 2 === 0 ? 'border-l-2' : 'border-r-2'} border-primary`}></div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-16 text-center border-t border-white/5 pt-16">
                    <p className="text-white/30 text-sm font-mono">More projects available on GitHub...</p>
                </div>
            </div>
        </main>
    );
};

export default Portfolio;
